import React from 'react';

import Skeleton from '../Skeleton';
import { ReactComponent as CheckSVG } from '../../assets/images/check-icon.svg';
import './BuildMethodSelector.css';

export type BuildMethod = 'DOCKERFILE' | 'BUILDPACK';

// Fixed card height. The subtitle slot is optional and the description wraps
// differently per option, so without it the cards would resize as the
// detection result arrives — the section would visibly jump.
const CARD_CLASSNAME =
  'build-method-card relative flex flex-col rounded-lg border p-4 min-h-[7.5rem] transition-colors';

interface BuildMethodOptionProps {
  method: BuildMethod;
  title: string;
  description: string;
  // Slot under the title: the detected stack, the reason the option is
  // disabled, or nothing.
  subtitle?: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  groupName: string;
  onSelect: (method: BuildMethod) => void;
}

// Selectable card: the whole card is the control, and the selection is shown
// by the accent border plus the check — no visible radio. The real radio is
// kept (sr-only, NOT display:none, so it stays focusable and arrow keys still
// move through the group); the focus ring is drawn on the card.
const BuildMethodOption = ({
  method,
  title,
  description,
  subtitle,
  selected,
  disabled,
  groupName,
  onSelect,
}: BuildMethodOptionProps) => {
  let stateClassName;

  if (disabled) {
    stateClassName = 'border-light-blue border-opacity-20 opacity-50 cursor-not-allowed';
  } else if (selected) {
    stateClassName = 'border-cta-green cursor-pointer';
  } else {
    stateClassName = 'border-light-blue border-opacity-30 hover:border-opacity-60 cursor-pointer';
  }

  return (
    <label
      className={`${CARD_CLASSNAME} ${stateClassName}`}
    >
      <input
        type="radio"
        className="sr-only"
        name={groupName}
        value={method}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(method)}
      />
      {selected && (
        <CheckSVG className="absolute top-4 right-4 text-cta-green" aria-hidden="true" />
      )}
      <div className="font-semibold pr-6">{title}</div>
      {subtitle && <div className="text-sm mt-1">{subtitle}</div>}
      <div className="text-sm opacity-70 mt-2">{description}</div>
    </label>
  );
};

// Placeholder with the same frame and height as a real card, so the section
// does not resize when the detection lands.
const BuildMethodOptionSkeleton = () => (
  <div className={`${CARD_CLASSNAME} border-light-blue border-opacity-20`}>
    <Skeleton className="h-5 w-32 rounded" />
    <Skeleton className="h-3 w-full rounded mt-3" />
    <Skeleton className="h-3 w-3/4 rounded mt-2" />
  </div>
);

interface BuildMethodSelectorProps {
  value: BuildMethod;
  onChange: (value: BuildMethod) => void;
  // A detection is in flight for the current branch + path.
  isDetecting?: boolean;
  // Whether a previous detection already produced an answer. Drives the
  // difference between "nothing to show yet" (skeleton) and "refreshing what
  // is already on screen" (dimmed cards).
  hasDetectionResult?: boolean;
  // Only the create form disables Dockerfile when the repository has none —
  // there is no point in creating an app whose first deploy already fails. In
  // Settings it stays selectable (the user may be about to commit one).
  dockerfileDisabled?: boolean;
  // Shown under the Dockerfile title: why it is disabled, typically.
  dockerfileSubtitle?: React.ReactNode;
  // Shown under the Buildpacks title: the detected stack, or the fact that
  // nothing was detected. The caller composes it — the component stays
  // ignorant of detection semantics, like it is for the Dockerfile side.
  buildpackSubtitle?: React.ReactNode;
  // The repository has a Dockerfile that this build method will ignore.
  showDockerfileIgnoredWarning?: boolean;
  // The route is off for this environment: the option is not offered at all.
  // Undefined means "no opinion" (Settings, where the app already has a stored
  // Build Method) and keeps both options available.
  buildpacksAvailable?: boolean;
  isDisabled?: boolean;
  groupName?: string;
}

const BuildMethodSelector = ({
  value,
  onChange,
  isDetecting,
  hasDetectionResult,
  dockerfileDisabled,
  dockerfileSubtitle,
  buildpackSubtitle,
  showDockerfileIgnoredWarning,
  buildpacksAvailable,
  isDisabled,
  groupName = 'buildMethod',
}: BuildMethodSelectorProps) => {
  // Nothing to choose from: render as it was before the route existed.
  if (buildpacksAvailable === false) {
    return null;
  }

  const onSelect = (method: BuildMethod) => {
    if (isDisabled || (method === 'DOCKERFILE' && dockerfileDisabled)) {
      return;
    }

    onChange(method);
  };

  // The status line keeps its height whether or not it has text, so the cards
  // never move when the detection starts or lands. Callers that never detect
  // (Settings) don't get the line at all.
  const statusLine = isDetecting === undefined ? null : (
    <div className="h-5 text-sm opacity-70">
      {isDetecting ? 'Detecting your app...' : ''}
    </div>
  );

  // Nothing detected yet: show the frames loading instead of rendering a
  // pre-selection that is about to change under the user's eyes.
  if (isDetecting && !hasDetectionResult) {
    return (
      <div className="space-y-4">
        {statusLine}
        <div className="grid grid-cols-2 gap-4" aria-busy="true">
          <BuildMethodOptionSkeleton />
          <BuildMethodOptionSkeleton />
        </div>
      </div>
    );
  }

  // Re-detecting keeps the current answer on screen and only dims it — the
  // cards stay where they are.
  return (
    <div className="space-y-4">
      {statusLine}
      <div
        role="radiogroup"
        aria-label="Build Method"
        aria-busy={isDetecting}
        className={`grid grid-cols-2 gap-4 transition-opacity ${isDetecting ? 'opacity-60' : ''}`}
      >
        <BuildMethodOption
          method="DOCKERFILE"
          title="Dockerfile"
          description="Use the Dockerfile from your repository."
          subtitle={dockerfileSubtitle}
          selected={value === 'DOCKERFILE'}
          disabled={!!isDisabled || !!dockerfileDisabled}
          groupName={groupName}
          onSelect={onSelect}
        />
        <BuildMethodOption
          method="BUILDPACK"
          title="Buildpacks"
          description="Auto-detect your stack and build without a Dockerfile (Node, Python, Go, PHP, Ruby, Java, .NET)."
          subtitle={buildpackSubtitle}
          selected={value === 'BUILDPACK'}
          disabled={!!isDisabled}
          groupName={groupName}
          onSelect={onSelect}
        />
      </div>
      {showDockerfileIgnoredWarning && value === 'BUILDPACK' && (
        <div className="text-sm opacity-70">
          Your Dockerfile won't be used — this app is built with Buildpacks.
        </div>
      )}
    </div>
  );
};

export default BuildMethodSelector;
